import { useEffect, useRef } from 'react'

export function isInteractiveElement(target: EventTarget | null): boolean {
  if (!target) return false
  const el = target as any
  if (typeof el.tagName !== 'string') return false
  const tag = el.tagName.toUpperCase()
  if (tag === 'INPUT' || tag === 'SELECT' || tag === 'BUTTON' || tag === 'TEXTAREA' || tag === 'A') {
    return true
  }
  if (typeof el.getAttribute === 'function' && el.getAttribute('role') === 'button') {
    return true
  }
  if (typeof el.closest === 'function' && el.closest('button, a, input, select, textarea, [role="button"]')) {
    return true
  }
  return false
}

export function useDragScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let isDown = false
    let startX = 0
    let scrollLeft = 0

    const onMouseDown = (e: MouseEvent) => {
      // Ignore right clicks and clicks on interactive controls
      if (e.button !== 0) return
      if (isInteractiveElement(e.target)) return

      isDown = true
      startX = e.pageX - el.offsetLeft
      scrollLeft = el.scrollLeft
      el.style.cursor = 'grabbing'
      el.style.userSelect = 'none'
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return
      e.preventDefault()
      const x = e.pageX - el.offsetLeft
      const walk = x - startX
      el.scrollLeft = scrollLeft - walk
    }

    const onMouseUp = () => {
      if (!isDown) return
      isDown = false
      el.style.cursor = ''
      el.style.userSelect = ''
    }

    el.addEventListener('mousedown', onMouseDown)
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)

    return () => {
      el.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  return ref
}
